/**
 * How an order reads on screen.
 *
 * A copy of src/lib/orderView.js in lumiere-mobile, where it is tested; kept
 * identical so a bill reads the same whether it was opened in the app or here.
 *
 * They used to compute these separately, and disagreed. The endpoint has
 * called the delivery charge three different things over the project's life
 * and sometimes leaves it out; order details worked it out from what the
 * other rows must add up to, while the bill printed 0 — so the same order
 * showed a bill whose lines did not add up to its own total. Dates, the
 * status wording and the customer's name drifted the same way.
 *
 * One reading of an order, used by both, so a customer comparing the two
 * never sees two different answers.
 */

/** A money field as a number, or null when absent or unreadable. '' is absent. */
export const toNumber = v => {
  if (v == null || v === '') return null;
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : null;
};

const round2 = n => Math.round(n * 100) / 100;

/** The delivery address, which arrives either as an object or as a JSON string. */
export const parseAddress = raw => {
  if (!raw) return null;
  if (typeof raw !== 'string') return raw;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

/**
 * The money rows.
 *
 * When no delivery charge is stored it is derived from total − subtotal +
 * discount, which is what those rows must add up to. Otherwise a missing
 * charge silently read as nothing and the bill no longer summed to its total.
 */
export const orderMoney = order => {
  const o = order || {};
  const subtotal = toNumber(o.subtotal);
  const discount = toNumber(o.discount_amount);
  const total = toNumber(o.total_amount);
  const named = toNumber(o.shipping_cost) ?? toNumber(o.shipping_amount) ?? toNumber(o.delivery_fee);
  const derived =
    named == null && subtotal != null && total != null ? round2(total - subtotal + (discount || 0)) : null;
  return { subtotal, discount, total, shipping: named != null ? named : derived };
};

/**
 * Who the order is for: the name on the delivery address first, because that
 * is the person at the door, then whatever name the order itself carries.
 */
export const customerName = order => {
  const o = order || {};
  const a = parseAddress(o.shipping_address);
  const fromAddr = a && (a.full_name || a.name || [a.first_name, a.last_name].filter(Boolean).join(' '));
  const fromOrder = o.customer_name || [o.first_name, o.last_name].filter(Boolean).join(' ');
  const name = String(fromAddr || fromOrder || '').trim();
  return name || null;
};

/** The translation key for a status, folding in the older names for the same states. */
const STATUS_KEY = {
  pending: 'status.pending',
  processing: 'status.processing',
  in_progress: 'status.processing',
  shipped: 'status.shipped',
  out_for_delivery: 'status.out_for_delivery',
  delivered: 'status.delivered',
  completed: 'status.delivered',
  cancelled: 'status.cancelled',
  refunded: 'status.refunded',
};

export const statusKey = status => STATUS_KEY[String(status || 'pending').toLowerCase()] || null;

/** Options for the placed-on date, so both screens print it the same way. */
export const DATE_FORMAT = { day: 'numeric', month: 'long', year: 'numeric' };
